import { Routes } from '@angular/router';
import { guestGuard } from './shared/services/auth/guest.guard';
import { authGuard } from './shared/services/auth/auth.guard';
import { roleGuard } from './shared/services/auth/role.guard';
import { UserType } from '@shared/types/user-type';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'business',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () => import('./login/login.component').then((c) => c.LoginComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'signup',
    loadComponent: () => import('./signup/signup.component').then((c) => c.SignupComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'business',
    loadComponent: () =>
      import('./features/business/business.component').then((c) => c.BusinessComponent),
  },
  {
    path: 'business/:id',
    loadComponent: () =>
      import('./features/business-detail/business-detail.component').then(
        (c) => c.BusinessDetailComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/container/container.component').then((c) => c.ContainerComponent),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        canActivate: [roleGuard(UserType.CONSUMER)],
        children: [
          {
            path: 'about-me',
            loadComponent: () =>
              import('./features/about-me/about-me.component').then((c) => c.AboutMeComponent),
          },
          {
            path: 'my-collection',
            loadComponent: () =>
              import('./features/my-collection/my-collection.component').then(
                (c) => c.MyCollectionComponent,
              ),
          },
          {
            path: 'my-appointments',
            loadComponent: () =>
              import('./features/my-appointments/my-appointments.component').then(
                (c) => c.MyAppointmentsComponent,
              ),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./features/settings/settings.component').then((c) => c.SettingsComponent),
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./features/dashboard/dashboard.component').then((c) => c.DashboardComponent),
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./features/notifications/notifications.component').then(
                (c) => c.NotificationsComponent,
              ),
          },
          {
            path: 'preferences',
            loadComponent: () =>
              import('./features/preferences/preferences.component').then(
                (c) => c.PreferencesComponent,
              ),
          },
          {
            path: 'reviews',
            loadComponent: () =>
              import('./features/reviews/reviews.component').then((c) => c.ReviewsComponent),
          },
        ],
      },
      {
        path: 'my-business',
        canActivate: [roleGuard(UserType.BUSINESS)],
        loadComponent: () =>
          import('./features/business-management/business-management.component').then(
            (c) => c.BusinessManagementComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./features/business-management/components/business-dashboard/business-dashboard.component').then(
                (c) => c.BusinessDashboardComponent,
              ),
          },
          {
            path: 'analytics',
            loadComponent: () =>
              import('./features/business-management/components/business-analytics/business-analytics.component').then(
                (c) => c.BusinessAnalyticsComponent,
              ),
          },
          {
            path: 'listings',
            loadComponent: () =>
              import('./features/business-management/components/business-listings/business-listings.component').then(
                (c) => c.BusinessListingsComponent,
              ),
          },
          {
            path: 'business-profile',
            loadComponent: () =>
              import('./features/business-management/business-profile/business-profile.component').then(
                (c) => c.BusinessProfileComponent,
              ),
          },
          {
            path: 'business-profile/:id',
            loadComponent: () =>
              import('./features/business-management/business-profile/business-profile.component').then(
                (c) => c.BusinessProfileComponent,
              ),
          },
          {
            path: 'reviews',
            loadComponent: () =>
              import('./features/business-management/business-reviews/business-reviews.component').then(
                (c) => c.BusinessReviewsComponent,
              ),
          },
          {
            path: 'reservations',
            loadComponent: () =>
              import('./features/business-management/business-reservations/business-reservations.component').then(
                (c) => c.BusinessReservationsComponent,
              ),
          },
          {
            path: 'events',
            loadComponent: () =>
              import('./features/events/events.component').then((c) => c.EventsComponent),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./features/business-management/business-settings/business-settings.component').then(
                (c) => c.BusinessSettingsComponent,
              ),
          },
          {
            path: 'company',
            loadComponent: () =>
              import('./features/business-management/company/company.component').then(
                (c) => c.CompanyComponent,
              ),
          },
          {
            path: 'business-menu',
            loadComponent: () =>
              import('./features/menu-management/business-menu/business-menu.component').then(
                (c) => c.BusinessMenuComponent,
              ),
          },
          {
            path: 'menu-management',
            loadComponent: () =>
              import('./features/menu-management/master-menu-layout/master-menu-layout.component').then(
                (c) => c.MasterMenuLayoutComponent,
              ),
          },
          {
            path: 'menu-management/item/new',
            loadComponent: () =>
              import('./features/menu-management/menu-item-entry/menu-item-entry.component').then(
                (c) => c.MenuItemEntryComponent,
              ),
          },
          {
            path: 'menu-management/item/:id',
            loadComponent: () =>
              import('./features/menu-management/menu-item-entry/menu-item-entry.component').then(
                (c) => c.MenuItemEntryComponent,
              ),
          },
          {
            path: 'menu-management/branch/:storeId',
            loadComponent: () =>
              import('./features/menu-management/business-menu/business-menu.component').then(
                (c) => c.BusinessMenuComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'search',
    loadComponent: () =>
      import('./features/business-search/business-search.component').then(
        (c) => c.BusinessSearchComponent,
      ),
  },
  {
    path: 'for-business',
    loadComponent: () =>
      import('./features/business-landing/business-landing.component').then(
        (c) => c.BusinessLandingComponent,
      ),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./page-not-found/page-not-found.component').then((c) => c.PageNotFoundComponent),
  },
];
